import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Trash2, ChevronRight } from 'lucide-react';
import usePipelines from '../../hooks/usePipelines';
import PipelineEditor from '../../components/crm/PipelineEditor';

export default function FastestFormCRMPipelines() {
  const navigate = useNavigate();
  const {
    pipelines,
    loading,
    createPipeline,
    updatePipeline,
    deletePipeline,
    fetchPipeline,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
  } = usePipelines();

  const [showEditor, setShowEditor] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleCreate = () => {
    setEditingPipeline(null);
    setIsCreating(true);
    setShowEditor(true);
  };

  const handleEdit = async (pipeline) => {
    const detail = await fetchPipeline(pipeline.id);
    if (detail) {
      setEditingPipeline(detail);
      setIsCreating(false);
      setShowEditor(true);
    }
  };

  const handleSave = async (data) => {
    if (isCreating) {
      const result = await createPipeline({
        name: data.name,
        description: data.description,
        stages: data.stages.map((s) => ({
          name: s.name,
          color: s.color,
          position: s.position,
          is_win_stage: s.is_win_stage,
          is_loss_stage: s.is_loss_stage,
        })),
      });
      if (result) {
        setShowEditor(false);
      }
    } else if (editingPipeline) {
      await updatePipeline(editingPipeline.id, {
        name: data.name,
        description: data.description,
      });

      // Handle stage changes
      const existingIds = new Set(editingPipeline.stages.map((s) => s.id));
      const newStageIds = new Set();

      for (const stage of data.stages) {
        if (stage.id && existingIds.has(stage.id)) {
          newStageIds.add(stage.id);
          const original = editingPipeline.stages.find((s) => s.id === stage.id);
          if (
            original &&
            (original.name !== stage.name ||
              original.color !== stage.color ||
              original.is_win_stage !== stage.is_win_stage ||
              original.is_loss_stage !== stage.is_loss_stage)
          ) {
            await updateStage(editingPipeline.id, stage.id, {
              name: stage.name,
              color: stage.color,
              is_win_stage: stage.is_win_stage,
              is_loss_stage: stage.is_loss_stage,
            });
          }
        } else {
          await createStage(editingPipeline.id, {
            name: stage.name,
            color: stage.color,
            position: stage.position,
            is_win_stage: stage.is_win_stage,
            is_loss_stage: stage.is_loss_stage,
          });
        }
      }

      // Delete removed stages
      for (const stage of editingPipeline.stages) {
        if (!newStageIds.has(stage.id)) {
          await deleteStage(editingPipeline.id, stage.id);
        }
      }

      // Reorder
      const reorderData = data.stages
        .filter((s) => s.id && !s.id.startsWith('new-'))
        .map((s) => ({ id: s.id, position: s.position }));
      if (reorderData.length > 0) {
        await reorderStages(editingPipeline.id, reorderData);
      }

      setShowEditor(false);
    }
  };

  const handleDelete = async (id) => {
    const success = await deletePipeline(id);
    if (success) {
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Pipelines
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage your sales pipelines and stages
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Pipeline
        </button>
      </div>

      {pipelines.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400 mb-4">No pipelines yet</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Create Your First Pipeline
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {pipelines.map((pipeline) => (
            <div
              key={pipeline.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => navigate(`/data/pipelines/${pipeline.id}`)}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {pipeline.name}
                    </h3>
                    {pipeline.is_default && (
                      <span className="px-2 py-0.5 text-xs bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400 rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  {pipeline.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {pipeline.description}
                    </p>
                  )}
                  <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{pipeline.stage_count} stages</span>
                    <span>{pipeline.deal_count} deals</span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(pipeline)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    title="Edit pipeline"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  {!pipeline.is_default && (
                    <button
                      onClick={() => setDeleteConfirm(pipeline.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                      title="Delete pipeline"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/data/pipelines/${pipeline.id}`)}
                    className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-md"
                    title="View pipeline"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="absolute inset-0 z-20" onClick={() => setDeleteConfirm(null)}></div>
          <div className="relative top-20 z-50 mx-auto p-5 border max-w-sm shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Delete Pipeline?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              This action cannot be undone. Only pipelines with no deals can be deleted.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline Editor Modal */}
      {showEditor && (
        <PipelineEditor
          pipeline={editingPipeline}
          isCreating={isCreating}
          onSave={handleSave}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}
